import { useState, useEffect } from 'react';
import { Trophy } from '@phosphor-icons/react';
import { API } from './constants';

export const SelfLearningPanel = () => {
  const [learningData, setLearningData] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    fetch(`${API}/email/learning/status`, { credentials: 'include' })
      .then(r => r.json()).then(setLearningData).catch(() => {});
  }, []);

  const triggerEvaluation = async () => {
    setEvaluating(true);
    try {
      const res = await fetch(`${API}/email/learning/evaluate`, { method: 'POST', credentials: 'include' });
      const result = await res.json();
      setLearningData(prev => ({ ...prev, last_evaluation: result }));
      const fresh = await fetch(`${API}/email/learning/status`, { credentials: 'include' });
      setLearningData(await fresh.json());
    } catch { /* ignore */ }
    setEvaluating(false);
  };

  if (!learningData) return null;

  return (
    <div className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-purple-50/30 p-5 mb-5" data-testid="self-learning-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">AI</div>
          <div>
            <h4 className="font-heading text-base font-semibold">Self-Learning A/B Engine</h4>
            <p className="text-xs text-zinc-500">Automatische optimalisatie van elke gegenereerde email</p>
          </div>
          <div className="flex items-center gap-1.5 ml-2">
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
            <span className="text-xs font-medium text-indigo-600">Actief</span>
          </div>
        </div>
        <button onClick={triggerEvaluation} disabled={evaluating} data-testid="trigger-evaluation-btn"
          className="text-xs px-3 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors">
          {evaluating ? 'Evalueren...' : 'Evalueer Nu'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          { l: 'A/B Test Ratio', v: learningData.ab_test_ratio, c: 'text-indigo-600' },
          { l: 'Geleerde Patronen', v: learningData.winning_insights, c: 'text-emerald-600' },
          { l: 'Evaluaties', v: learningData.total_evaluations, c: 'text-zinc-700' },
          { l: 'Wacht op Evaluatie', v: learningData.pending_evaluations, c: 'text-amber-600' },
          { l: 'Totaal Inzichten', v: learningData.total_insights, c: 'text-purple-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white/80 border border-zinc-200 p-3 text-center">
            <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
          </div>
        ))}
      </div>

      {learningData.top_techniques?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-indigo-700 uppercase tracking-wider mb-2">Winnende Technieken</div>
          <div className="flex flex-wrap gap-2">
            {learningData.top_techniques.map((t, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-indigo-200 px-3 py-1.5 text-xs" data-testid={`winning-technique-${i}`}>
                <Trophy size={12} className="text-amber-500" />
                <span className="font-medium">{t.pattern}</span>
                <span className="text-indigo-500">{t.wins}W/{t.total_tests}T</span>
                {t.avg_uplift > 0 && <span className="text-emerald-600">+{t.avg_uplift}%</span>}
                {t.example_subject && <span className="text-zinc-400 max-w-48 truncate" title={t.example_subject}>"{t.example_subject}"</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {learningData.recent_evaluations?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-indigo-700 uppercase tracking-wider mb-2">Recente Evaluaties</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {learningData.recent_evaluations.slice(0, 5).map((e, i) => (
              <div key={i} className="flex items-center gap-3 text-xs bg-white/60 border border-zinc-100 px-3 py-1.5">
                <span className={`font-medium ${e.winner === 'test' ? 'text-emerald-600' : 'text-zinc-500'}`}>
                  {e.winner === 'test' ? 'Variant wint' : 'Controle wint'}
                </span>
                <span className="text-zinc-400">|</span>
                <span className="text-indigo-500">{e.test_technique}</span>
                <span className="text-zinc-400">|</span>
                <span className={e.winner === 'test' ? 'text-emerald-600' : 'text-red-400'}>
                  {e.winner === 'test' ? '+' : '-'}{e.uplift_pct}%
                </span>
                <span className="text-zinc-400 ml-auto">{e.evaluated_at ? new Date(e.evaluated_at).toLocaleDateString('nl-NL') : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {learningData.top_techniques?.length === 0 && learningData.recent_evaluations?.length === 0 && (
        <p className="text-xs text-zinc-500 italic">Het systeem leert automatisch zodra emails worden verzonden. 20% krijgt een experimentele variant, na 24u worden resultaten geëvalueerd.</p>
      )}
    </div>
  );
};
